<template>
	<div
		v-if="showAlert"
		class="new-job-box js-right-first-card px-3 py-2 mt-11px"
	>
		<img
			class="firstside-pos"
			src="../../assets/newdesign/jobsearch/firstSide.svg"
			alt=""
		/>
		<div class="js-grey-txt w-75 mb-2">
			<template v-if="isLocationLoading">
				<b-skeleton class="mb-4 mt-2" width="100%"></b-skeleton>
			</template>
			<template v-else>
				Be the first to see new jobs in {{ fetchedLocation }}
			</template>
		</div>
		<div class="mt-2 text-left w-100">
			<template v-if="isLocationLoading">
				<b-skeleton class="mb-4" type="input"></b-skeleton>
			</template>
			<template v-else>
				<InputField
					:vmodel="email"
					@select-value="updateEmail"
					@handle-blur="$v.email.$touch()"
					@keyup.native.enter="!$v.email.$invalid && getLocationDetails()"
					:label="'Email'"
					:id="'activate-email'"
					:type="'email'"
					:mandatory="true"
					:placeholder="'Enter'"
					:showError="$v.email.$dirty && $v.email.$invalid"
					:disabled="isLoggedIn"
				/>
				<div
					class="newInt-required-validation-font mt-1 error-ml-1"
					style="color: #ff0000; font-size: 11px"
					v-if="$v.email.$dirty && !$v.email.required"
				>
					Required
				</div>
				<div
					class="newInt-required-validation-font mt-1 error-ml-1"
					style="color: #ff0000; font-size: 11px"
					v-else-if="
						$v.email.$dirty && (!$v.email.email || !$v.email.alphaEmail)
					"
				>
					Email is invalid
				</div>
				<div
					class="newInt-required-validation-font mt-1 error-ml-1"
					style="color: #ff0000; font-size: 11px"
					v-else-if="$v.email.$dirty && !$v.email.maxLength"
				>
					Cannot enter more than 64 characters
				</div>
			</template>
		</div>

		<div class="d-flex align-items-center justify-content-between mt-2">
			<div class="js-small-txt pr-3">
				On activating, you agree to the
				<span class="js-underline-txt"
					>Terms and Conditions & Privacy Policy</span
				>
				of Hire3x.com
			</div>
			<template v-if="isLocationLoading">
				<b-skeleton type="button"></b-skeleton>
			</template>
			<b-button
				:class="activateLoader ? 'loader-class job defaultCursor ' : 'pointer'"
				v-else
				class="candash-addBtn-js up-btn d-flex align-items-center position-relative"
				:disabled="$v.email.$invalid || activateLoader"
				@click="getLocationDetails()"
				style="background: transparent"
			>
				<template v-if="activateLoader">
					<div class="loader-pos-new">
						<Loader />
					</div>
				</template>
				<template v-else>
					Activate
					<img
						class="ml-1"
						src="../../assets/newdesign/jobsearch/whiteArrow.svg"
						alt=""
					/>
				</template>
			</b-button>
		</div>
	</div>
</template>

<script>
import { required, email, maxLength } from "vuelidate/lib/validators";
import { hasAccess } from "@/utils/tokenCheck";
import toast from "@/components/mixin/toast";
import EventBus from "@/utils/EventBus";
import InputField from "@/components/InputComponents/Input.vue";
import Loader from "@/components/fixedcomponents/Loader.vue";
import {
	getJobAlert,
	getJobPreference,
	checkJobAlert,
	getLocation,
} from "@/utils/apiFunctions";
import { mapGetters } from "vuex";

export default {
	name: "JobAlert",
	props: {
		isPersistent: {
			type: Boolean,
			default: false,
		},
	},
	mixins: [toast],
	data() {
		return {
			email: null,
			prefCity: null,
			showAlert: false,
			startDownload: false,
			isLocationLoading: false,
			fetchedLocation: "",
			lat: null,
			lng: null,
			activateLoader: false,
		};
	},
	created() {
		EventBus.$on("job-preference-updated", this.fetchJobPreference);
	},
	beforeDestroy() {
		EventBus.$off("job-preference-updated", this.fetchJobPreference);
	},
	components: {
		InputField,
		Loader,
	},
	computed: {
		...mapGetters("userVUEX", {
			userinfo: "getUserInfo",
			userEmail: "getUserEmail",
		}),
		isLoggedIn() {
			return hasAccess();
		},
	},
	watch: {
		userinfo: {
			immediate: true,
			deep: true,
			handler(val) {
				if (hasAccess() && val?.email && !this.email) {
					this.initializeUser(val.email);
				}
			},
		},
	},
	mounted() {
		if (hasAccess()) {
			const email = this.userinfo?.email || this.userEmail;
			if (email && !this.email) {
				this.initializeUser(email);
			}
		} else {
			// Guest user — always show the job alert card
			this.showAlert = true;
			this.getLocationDetails(true);
		}
	},
	validations() {
		return {
			email: {
				required,
				email,
				maxLength: maxLength(64),
				alphaEmail: (value) => {
					if (value === null) return true;
					return /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(value);
				},
			},
		};
	},
	methods: {
		async initializeUser(email) {
			this.email = email;
			this.$nextTick(() => {
				if (this.$v?.email) this.$v.email.$touch();
			});
			await this.fetchJobPreference();
			this.checkJobAlertData(email);
		},
		updateEmail(val) {
			this.email = val;
			this.$v.email.$touch();
		},
		openLoginModal() {
			EventBus.$emit("open-login-modal");
		},
		async fetchJobPreference() {
			try {
				const emailID = this.userinfo?.email;
				if (!emailID) return;
				const res = await getJobPreference(emailID);
				if (res.status === 200) {
					const data = res.data.data;
					if (data?.willingToRelocateTo?.length > 0) {
						this.prefCity = data.willingToRelocateTo[0];
						this.fetchedLocation = this.prefCity;
						this.showAlert = true; // Show if pref exists
					} else {
						// Preference deleted or empty
						this.prefCity = null;
						// Revert to geolocation if available
						if (this.lat && this.lng) {
							await this.getUserLocation(this.lat, this.lng);
						} else {
							await this.getLocationDetails(true);
						}
					}
				}
			} catch (error) {
				console.error("Error fetching job preference:", error);
			}
		},
		async getLocationDetails(onlyProcessLocation = false) {
			if (this.isLocationLoading || this.activateLoader) return;
			if (onlyProcessLocation) {
				this.isLocationLoading = true;
			} else {
				this.activateLoader = true;
			}

			// If user has a job preference city, skip geolocation and call API directly
			if (this.prefCity) {
				this.isLocationLoading = false;
				if (!onlyProcessLocation) {
					await this.getJobAlertData();
				}
				return;
			}
			// Otherwise fetch geolocation and send lat/lng
			if ("geolocation" in navigator) {
				navigator.geolocation.getCurrentPosition(
					async (position) => {
						this.lat = position.coords.latitude;
						this.lng = position.coords.longitude;
						if (this.lat && this.lng) {
							if (onlyProcessLocation) {
								await this.getUserLocation(this.lat, this.lng);
							} else {
								await this.getJobAlertData(this.lat, this.lng);
							}
						}
						this.isLocationLoading = false;
						this.activateLoader = false;
					},
					(error) => {
						console.warn("Geolocation Error:", error.code, error.message);

						this.isLocationLoading = false;
						this.activateLoader = false;
						// If landing fetch and no preference found, hide the modal
						if (onlyProcessLocation && !this.prefCity) {
							this.showAlert = false;
						} else if (!onlyProcessLocation) {
							// For manual clicks, always show error modal
							this.errorModal(
								"Error",
								"Please enable location permission to continue!"
							);
						}
					}
				);
			} else {
				this.isLocationLoading = false;
				this.activateLoader = false;
				if (onlyProcessLocation && !this.prefCity) {
					this.showAlert = false;
				}
			}
		},
		async getJobAlertData(latitude, longitude) {
			this.activateLoader = true;
			try {
				let payload;
				if (this.prefCity) {
					console.log("Using preferred city:", this.prefCity);
					payload = {
						email: this.email,
						city: this.prefCity,
					};
				} else {
					const finalLat = latitude || this.lat;
					const finalLng = longitude || this.lng;
					payload = {
						email: this.email,
						latitude: finalLat ? finalLat.toString() : "",
						longitude: finalLng ? finalLng.toString() : "",
					};
				}

				const response = await getJobAlert(payload);

				if (response.status == 200) {
					this.successModal("Success", "Subscribed to job alerts");
					if (!this.isPersistent) {
						this.showAlert = false;
					}
					this.email = null;
					this.$v.email.$reset();
				}
				 else {
						this.errorModal("Error", "Something went wrong..");
					}
			} catch (error) {
				this.errorModal("Error", "Something went wrong..");
				console.error("getJobAlert error:", error);
			} finally {
				this.activateLoader = false;
			}
		},

		async checkJobAlertData(emailID) {
			try {
				const payload = { email: emailID };
				const response = await checkJobAlert(payload);
				if (response.status === 404) {
					this.showAlert = true;
					this.getLocationDetails(true);
				} else {
					if (!this.isPersistent) {
						this.showAlert = false;
					} else {
						this.showAlert = true;
						this.getLocationDetails(true); // Still fetch location for display
					}
				}
			} catch (error) {
				console.error("Error:", error);
			}
		},
		async getUserLocation(latitude, longitude) {
			try {
				let payload = {
					latitude: latitude.toString(),
					longitude: longitude.toString(),
				};
				const response = await getLocation(payload);

				if (response.status == 200) {
					const data = response.data.data;
					if (data.city && data.state) {
						this.fetchedLocation = `${data.city}, ${data.state}`;
					} else {
						this.fetchedLocation = data.formatted_address;
					}
				} else {
					this.startDownload = false;
				}
			} catch (error) {
				console.error("Error fetching location:", error);
			}
		},
	},
};
</script>
<style scoped>
.activate-button:disabled {
	opacity: 0.5;
	pointer-events: none;
}
</style>
